import React from 'react';
import style from './accountLinkForm.module.scss';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import { FormValidator, ValidationRule } from 'acscp/components/common/helpers/formValidator';
import { StringValidationChecks } from 'acscp/components/common/helpers/validationChecks';
import { InputWithValidation } from 'acscp/components/common/validation/inputWithValidation';
import { Dispatch } from 'redux';
import { AppState, CustomerContact } from 'acscp/store';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { RequestStateProps } from '../common/helpers/requestStateProps';
import { AccountLinksRequest, CustomerContactDetailsRequest, RequestClass } from 'acscp/api/requests';
import { AddAccountLinkConfirmationDialog } from './addAccountLinkConfirmationDialog';
import { FetchRequest } from 'acscp/actions';
import { AddAccountLinkRequest } from 'acscp/api/requests/addAccountLinkRequest';
import { AccountLink } from 'acscp/api/models/accountLink';

const messages = defineMessages({
    emailError: {
        id: 'components.accountLinks.accountLinkForm.invalidEmail',
        defaultMessage: 'Please enter a valid email.'
    },
    duplicateEmail: {
        id: 'components.accountLinks.accountLinkForm.duplicateEmail',
        defaultMessage: 'A link for this email already exists.'
    },
    emailPlaceholder: {
        id: 'components.accountLinks.accountLinkForm.emailPlaceholder',
        defaultMessage: 'EMAIL'
    }
});

type AccountLinkFormOwnProps = InjectedIntlProps;

interface AccountLinkFormStateProps extends RequestStateProps {
    accountLinks: AccountLink[];
    customerContact?: CustomerContact;
}

interface AccountLinkFormInternalState {
    emailAddress: string;
    showErrors: boolean;
    isConfirmationDialogOpen: boolean;
}

interface AccountLinkFormDispatchProps {
    addAccountLink: (emailAddress: string) => void;
    fetchAccountLinks: () => void;
    fetchCustomerContactDetails: () => void;
}

type AccountLinkFormProps = AccountLinkFormOwnProps & AccountLinkFormStateProps & AccountLinkFormDispatchProps;

export class AccountLinkFormPresentational extends
    React.Component<AccountLinkFormProps, AccountLinkFormInternalState> {

    private validator: FormValidator;

    constructor(props: AccountLinkFormProps) {
        super(props);

        this.validator = new FormValidator(this.getValidationRules());

        this.state = {
            emailAddress: '',
            showErrors: false,
            isConfirmationDialogOpen: false
        };
    }

    private getValidationRules(): ValidationRule[] {
        const translator = this.props.intl.formatMessage;
        return [
            {
                validatedPropertyName: 'emailAddress',
                validationCheck: StringValidationChecks.isEmail(() => this.state.emailAddress),
                errorMessage: translator(messages.emailError)
            },
            {
                validatedPropertyName: 'emailAddress',
                validationCheck: StringValidationChecks.stringNotContainedIn(
                    () => this.state.emailAddress,
                    () => this.getInvalidEmails()
                ),
                errorMessage: translator(messages.duplicateEmail)
            }
        ];
    }

    componentDidMount(): void {
        this.props.fetchAccountLinks();
        this.props.fetchCustomerContactDetails();
    }

    openConfirmationDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (this.validator.isStateValid()) {
            this.setState({ isConfirmationDialogOpen: true });
        }
        this.setState({ showErrors: true });
    }

    closeConfirmationDialog = () => {
        this.setState({isConfirmationDialogOpen: false});
    }

    addAccountLink = () => {
        this.props.addAccountLink(this.state.emailAddress);
    }

    emailChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ emailAddress: event.target.value })

    getInvalidEmails(): string[] {
        const emailArray = [...this.props.accountLinks.map(t => t.emailAddress)];
        const customerContact = this.props.customerContact;
        if (customerContact && customerContact.email) {
            emailArray.push(customerContact.email);
        }
        return emailArray;
    }

    render(): JSX.Element {
        const { intl, requestError } = this.props;
        const { emailAddress, isConfirmationDialogOpen } = this.state;

        const validationErrors = this.validator.getDisplayedErrors(this.state.showErrors, requestError);

        return (
            <div
                className={style.Form}
            >
                <div className={style.Message}>
                    <FormattedMessage
                        id={'components.accountLinks.accountLinkForm.message'}
                        defaultMessage={
                            'In order to automatically share all of your future quotes and bookings with another JetPortal Account ' +
                            'Holder, enter their email address into the field below and click ‘Link’. ' +
                            'If they don’t have an account they’ll be prompted to create one.'
                        }
                    />
                </div>
                <InputWithValidation
                    type="email"
                    name="emailAddress"
                    id="emailAddress"
                    value={emailAddress}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    onChange={this.emailChanged}
                    validationErrors={validationErrors}
                    autoFocus={true}
                    className={style.EmailInput}
                />
                <button className={style.Button} onClick={this.openConfirmationDialog}>
                    <FormattedMessage
                        id={'components.accountLinks.accountLinkForm.linkButtonText'}
                        defaultMessage={'Link'}
                    />
                </button>
                <AddAccountLinkConfirmationDialog
                    isOpen={isConfirmationDialogOpen}
                    closeDialog={this.closeConfirmationDialog}
                    email={emailAddress}
                    addAccountLink={() => this.props.addAccountLink(emailAddress)}
                />
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState,
    ownProps: {},
    getRequestError: (request: RequestClass<any>) => RequestStateProps
): AccountLinkFormStateProps {
    return {
        ...getRequestError(AddAccountLinkRequest),
        accountLinks: state.accountLinks.accountLinks,
        customerContact: state.customerContact.customer
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): AccountLinkFormDispatchProps {
    return {
        addAccountLink: (emailAddress: string) =>
            dispatch(new FetchRequest(new AddAccountLinkRequest(emailAddress))),
        fetchAccountLinks: () => dispatch(new FetchRequest(new AccountLinksRequest())),
        fetchCustomerContactDetails: () => dispatch(new FetchRequest(new CustomerContactDetailsRequest())),
    };
}

export const AccountLinkForm = connectWithRequests(mapStateToProps, mapDispatchToProps)
    (injectIntl(AccountLinkFormPresentational));
